import { Stethoscope, Scale, ShieldPlus, HeartPulse, ClipboardList, Sparkles, Handshake, BadgeCheck, Users, MessagesSquare } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  useSeoMeta, LandingHero, CoverageGrid, WhyUsSection,
  TestimonialsSection, FaqSection, Breadcrumb,
} from "../lib/landingShared";

const HERO_IMG = "https://images.pexels.com/photos/1108099/pexels-photo-1108099.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=700";

const COVERAGE = [
  { icon: Stethoscope,     title: "Spese veterinarie",             desc: "Rimborso per visite, esami, interventi chirurgici e ricoveri presso strutture veterinarie." },
  { icon: HeartPulse,      title: "Prevenzione e vaccinazioni",    desc: "Alcune polizze includono un pacchetto per vaccini di richiamo, sterilizzazione e cure preventive." },
  { icon: Scale,           title: "Responsabilità civile",         desc: "Copre i danni causati dall'animale a persone, cose o altri animali in ambito pubblico e privato." },
  { icon: ShieldPlus,      title: "Assistenza h24",                desc: "Pronto intervento veterinario, consulenze telefoniche, custodia dell'animale in caso di ricovero del proprietario." },
  { icon: ClipboardList,   title: "Tutela legale",                 desc: "Spese di difesa in controversie legate alla proprietà o alla conduzione dell'animale." },
  { icon: HeartPulse,      title: "Patologie e infortuni",         desc: "Interventi d'urgenza a seguito di traumi, infortuni o diagnosi di patologie previste in polizza." },
];

const WHY = [
  { icon: Handshake,  title: "Consulenza personalizzata",  desc: "Razza, età, stile di vita: la copertura giusta parte dalle caratteristiche del tuo animale." },
  { icon: Sparkles,   title: "Garanzie modulabili",         desc: "Solo spese veterinarie, solo RC, o pacchetto completo con assistenza: scegli in base alle tue priorità." },
  { icon: BadgeCheck, title: "Chiarezza sulle clausole",    desc: "Massimali, franchigie, periodo di carenza, esclusioni: te lo spieghiamo prima della firma." },
  { icon: Users,      title: "Più compagnie a confronto",   desc: "Confrontiamo le condizioni delle principali compagnie del ramo pet e proponiamo la più equilibrata." },
];

const FAQS = [
  { q: "L'assicurazione per cani e gatti è obbligatoria?",
    a: "No, in Italia l'assicurazione cane/gatto non è obbligatoria per legge, con l'eccezione dei cani classificati a rischio elevato (razze da guardia o particolari) per i quali alcune regioni richiedono una copertura di responsabilità civile. Ricordiamo però che il proprietario risponde civilmente e penalmente dei danni causati dal proprio animale: una polizza RC è quindi fortemente consigliata anche quando non obbligatoria." },
  { q: "Quali spese veterinarie sono coperte?",
    a: "Le polizze pet più complete rimborsano visite specialistiche, esami diagnostici (radiografie, ecografie, analisi), interventi chirurgici, ricoveri, terapie farmacologiche, cure oncologiche, fisioterapia. Alcune includono pacchetti di prevenzione (vaccini, sterilizzazione, esami periodici). La combinazione esatta e i massimali dipendono dalla polizza scelta: te li illustriamo nel dettaglio in consulenza." },
  { q: "Come funziona la copertura RC del proprietario?",
    a: "La responsabilità civile risarcisce i danni che il tuo cane o gatto causa a terzi: morsi, aggressioni, danni a cose (mobili altrui, veicoli, oggetti), o danni ad altri animali. Copre sia il proprietario che eventuali persone temporaneamente in custodia dell'animale (dog-sitter, familiari). Il massimale minimo consigliato è di 500.000 €, ma per razze grandi o attive è meglio salire a 1 milione." },
  { q: "Ci sono limiti di età o razza?",
    a: "Sì. La maggior parte delle compagnie assicura cani e gatti a partire dai 60-90 giorni di età, con limiti superiori che variano tra i 7 e i 10 anni per la prima sottoscrizione (una volta assicurato, il contratto può essere rinnovato oltre). Alcune razze considerate a rischio elevato (Rottweiler, Pit Bull, Amstaff, ecc.) possono avere condizioni particolari o esclusioni. In consulenza verifichiamo le opzioni sulla base del tuo animale." },
  { q: "Quanto costa una polizza cane o gatto?",
    a: "Il premio dipende da specie, razza, età, garanzie scelte e massimali. Indicativamente, una polizza base di responsabilità civile parte da circa 60-100 € all'anno. Una copertura completa con spese veterinarie e assistenza può andare dai 200 ai 500 € annui, a seconda del pacchetto. Richiedendo una consulenza ti forniamo un preventivo personalizzato senza impegno." },
];

const TRUST = [
  { value: "1958", label: "Anno di fondazione" },
  { value: "3",    label: "Generazioni di esperienza" },
  { value: "24h",  label: "Risposta alla richiesta" },
];

const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": FAQS.map((f) => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })),
};

export default function CaniGatti() {
  useSeoMeta({
    title: "Assicurazione Cani e Gatti — Spese veterinarie e RC | Da Pra Assicurazioni",
    description: "Assicurazione per cani e gatti su misura: spese veterinarie, responsabilità civile, tutela legale e assistenza. Consulenza dedicata, agenzia assicurativa plurimandataria dal 1958.",
    keywords: "assicurazione cani, assicurazione gatti, assicurazione animali domestici, polizza cane, polizza gatto, spese veterinarie, RC animali",
    canonical: "https://dapraassicurazioni.it/assicurazione-cani-gatti",
    jsonLd: JSON_LD, jsonLdId: "ld-cani-gatti",
  });

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <Breadcrumb current={{ label: "Assicurazione Cani e Gatti", href: "/assicurazione-cani-gatti" }} />

      <LandingHero
        eyebrow="Cani e Gatti · Dal 1958"
        title={<>Prenditi cura di chi <span className="italic">fa parte della famiglia.</span></>}
        description="Una copertura pensata per i tuoi animali domestici: spese veterinarie, responsabilità civile del proprietario, assistenza h24 e tutela legale. La costruiamo insieme sulle caratteristiche del tuo cane o gatto."
        imageSrc={HERO_IMG}
        imageAlt="Assicurazione cani e gatti — Da Pra Assicurazioni"
        ctaPrimaryLabel="Richiedi un preventivo"
        ctaPrimaryHref="/preventivo?type=pet"
        trustItems={TRUST}
      />

      <CoverageGrid
        eyebrow="Cosa puoi proteggere"
        heading="Garanzie modulabili per il tuo animale."
        intro="Componi la copertura partendo dalle esigenze reali: dalla responsabilità civile al pacchetto completo con spese veterinarie e assistenza."
        items={COVERAGE}
      />

      <WhyUsSection
        eyebrow="Il nostro approccio"
        heading="Una polizza costruita sull'animale."
        body={[
          "Da tre generazioni accompagniamo famiglie nella scelta delle coperture assicurative. Conosciamo le specificità delle polizze pet e le condizioni delle compagnie che operano nel ramo.",
          "Ogni animale è diverso: razza, età, temperamento, ambiente in cui vive. Partiamo da qui e costruiamo la copertura più adatta, con la stessa cura che riservi al tuo cane o gatto.",
        ]}
        items={WHY}
      />

      <TestimonialsSection heading="La fiducia dei nostri clienti." />
      <FaqSection heading="Domande frequenti sull'assicurazione cani e gatti." faqs={FAQS} />

      <Footer />
    </div>
  );
}
